import { computed } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { I18n, type TranslateOptions } from 'i18n-js'
import { en } from '../locales/en'
import { de } from '../locales/de'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { usePreferencesStore } from '@/stores/preferences.ts'
import { catalogQuery } from '@/data/catalogClient.ts'
import { useUserSettingsStore } from '@/stores/user-settings'
import { GET_VOLUME_SUFFIXES } from '@/queries'
import { isFreeFormListLabel, formatPrice as formatPriceForLanguage, type ListLabel } from '@/utils'

export type TranslationKey = keyof typeof de
export type Language = 'en' | 'de'
export type TranslationUnit = {
  key: TranslationKey
  context: { [key: string]: string | number }
}
export const LANG_TRANSLATION_MAP: { [key in Language]: TranslationKey } = {
  de: 'languagesGerman',
  en: 'languagesEnglish',
}

// Initialize i18n instance
const i18n = new I18n({
  en,
  de,
})
i18n.locale = 'de'
i18n.enableFallback = true
i18n.defaultLocale = 'de'

export type Iso316611alpha2Codes =
  | 'de'
  | 'fr'
  | 'zh'
  | 'tw'
  | 'jp'
  | 'it'
  | 'es'
  | 'eu'
  | 'gb'
  | 'ko'
  | 'us'

type VolumeSuffixEntry = { name: string; abbreviation: string }

export type LocalizationStore = Omit<
  ReturnType<typeof useLocalizationStore>,
  keyof ReturnType<typeof defineStore>
>

export const useLocalizationStore = defineStore('localization', () => {
  const { setLanguage } = usePreferencesStore()
  const { language } = storeToRefs(usePreferencesStore())

  const volumeSuffixMap = new Map<string, VolumeSuffixEntry>([['', { name: '', abbreviation: '' }]])

  const getVolumeSuffixFromId = (id: string): VolumeSuffixEntry => {
    return volumeSuffixMap.get(id) || { name: '', abbreviation: '' }
  }

  const t = (key: TranslationKey | TranslationUnit, options?: TranslateOptions): string => {
    if (typeof key === 'object') {
      const { key: translationKey, context } = key
      return i18n.t(translationKey as string, {
        ...options,
        ...context,
      })
    }
    return unescape(i18n.t(key as string, options))
  }

  const tListLabel = (label: ListLabel): string => {
    if (isFreeFormListLabel(label)) {
      return label[language.value]
    }
    return t(label)
  }

  const applyLanguage = (l: Language) => {
    dayjs.locale(l)
    i18n.locale = l
  }

  const changeLanguage = async (l: Language) => {
    applyLanguage(l)
    await setLanguage(l)
    // Queue the server push on the offline-durable settings stack; drains now if
    // online, otherwise replays on reconnect.
    useUserSettingsStore().setLanguage(l)
  }

  // Reflect the server's language on this device at startup. The user-settings overlay
  // already surfaces a pending offline change (local choice wins), so folding its value
  // into local state pulls the server value when there is nothing pending and is a no-op
  // when there is.
  const reconcileLanguageFromServer = () => {
    const serverLanguage = useUserSettingsStore().settings.language
    if (
      (serverLanguage === 'de' || serverLanguage === 'en') &&
      serverLanguage !== language.value
    ) {
      applyLanguage(serverLanguage)
      void setLanguage(serverLanguage)
    }
  }

  const loadVolumeSuffixes = async () => {
    const result = await catalogQuery({
      query: GET_VOLUME_SUFFIXES,
      variables: {},
      fetchPolicy: 'network-only',
    })
    const suffixes = result?.data?.volumeSuffixes ?? []
    if (suffixes.length === 0) {
      return
    }
    volumeSuffixMap.clear()
    for (const suffix of suffixes) {
      volumeSuffixMap.set(suffix.id, {
        name: suffix.name ?? 'NOT_SET',
        abbreviation: suffix.abbreviation ?? 'OH_NO',
      })
    }
  }

  const init = async () => {
    if (language.value) {
      applyLanguage(language.value)
    }
    await loadVolumeSuffixes()
  }

  const formatPrice = (price: string | number | null | undefined) =>
    formatPriceForLanguage(price, language.value)

  const numberLocale = computed(() => (language.value === 'de' ? 'de-DE' : 'en-US'))

  const formatNumber = (value: number, fractionDigits = 0) =>
    value.toLocaleString(numberLocale.value, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })

  return {
    t,
    tListLabel,
    changeLanguage,
    reconcileLanguageFromServer,
    language,
    init,
    getVolumeSuffixFromId,
    loadVolumeSuffixes,
    formatPrice,
    numberLocale,
    formatNumber,
  }
})

const matchHtmlEntity =
  /&(?:amp|#38|lt|#60|gt|#62|apos|#39|quot|#34|nbsp|#160|copy|shy|#169|reg|#174|hellip|#8230|#x2F|#47);/g

const htmlEntities: { [key: string]: string } = {
  '&amp;': '&',
  '&#38;': '&',
  '&lt;': '<',
  '&#60;': '<',
  '&gt;': '>',
  '&#62;': '>',
  '&apos;': "'",
  '&#39;': "'",
  '&quot;': '"',
  '&#34;': '"',
  '&nbsp;': ' ',
  '&#160;': ' ',
  '&copy;': '©',
  '&shy;': '­',
  '&#169;': '©',
  '&reg;': '®',
  '&#174;': '®',
  '&hellip;': '…',
  '&#8230;': '…',
  '&#x2F;': '/',
  '&#47;': '/',
}

const unescapeHtmlEntity = (m: string) => htmlEntities[m]

const unescape = (text: string) => text.replace(matchHtmlEntity, unescapeHtmlEntity)
